import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SORTES_DE_LIEN, type SorteDeLien } from '@nextilt/commun';
import { api, EchecApi } from '../partage/api/client';
import type { Boucle } from '../partage/api/types';
import { cles, relireApres } from '../partage/relectures';
import { useEchap } from '../partage/useEchap';
import { IconeLier } from '../partage/icones';
import { estUnCycle, RefusDeCycle, useSortieDeBoucle } from '../moteur/conditions';
import {
  bullesReliees,
  CLIQUE_LA_PREMIERE,
  DEJA_RELIEE,
  FINI,
  laPremiere,
  LIER,
  LIER_TITRE,
  LIEN_BOUCLERAIT,
  LIEN_DANS_L_AUTRE_SENS,
  LIEN_PAS_POSE,
  lienChange,
  MOTS_DES_SORTES_A_LIER,
  PAS_A_ELLE_MEME,
  PUIS_LA_SORTE,
  PUIS_LES_BULLES,
  RELIEE,
  sensDuLien,
  TOUCHE_ECHAP_EN_CLAIR,
} from '../partage/vocabulaire';
import { issueDuClic, type BoutsDuLien } from './clicDeLier';
import './Lier.css';

/**
 * **« Lier », sur la carte** (chantier liens-entre-bulles, ticket 04, décisions 2, 3 et 11 du
 * cadrage) : le bouton « Lier » ou la touche J, puis on clique la première bulle, on choisit la
 * sorte dans le bandeau (Attend, Sert à, Touche à), et chaque bulle cliquée est reliée à la
 * première ; le lien apparaît aussitôt. Échap ou « Fini » arrête. Comme Hiérarchiser
 * (`useHierarchisation`) : un mode à côté de tout, et pendant ce temps un clic sur une bulle ne
 * fait que la désigner.
 *
 * La pose est celle du serveur (`POST /api/dependances`, ticket 01), qui écrit au journal ; la
 * règle du clic est dans `clicDeLier.ts` ; un refus de boucle se dit comme dans la fiche
 * (`RefusDeCycle`, ses issues dans `useSortieDeBoucle`), sans arrêter « Lier ». La sorte se
 * change en cours de route : le bandeau reste ouvert, la suivante vaut pour les clics d'après.
 *
 * `possible` : la Carte, à l'ordinateur, dans un mode qui garde J ; faux, « Lier » s'arrête.
 * `feuille` : en changer arrête aussi.
 */
export function useLiaison(possible: boolean, feuille: string) {
  const client = useQueryClient();
  const [actif, setActif] = useState(false);
  const [premiere, setPremiere] = useState<string | null>(null);
  const [sorte, setSorte] = useState<SorteDeLien | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [reliees, setReliees] = useState(0);

  const arreter = useCallback(() => {
    setActif(false);
    setPremiere(null);
    setSorte(null);
    setMessage(null);
    setReliees(0);
  }, []);

  // Échap arrête, comme Hiérarchiser : à côté de tout. Un refus de boucle ouvert l'entend d'abord.
  useEchap(actif, arreter, 'a-cote');

  // Quitter la Carte, la feuille, ou un mode sans J : « Lier » s'arrête.
  useEffect(() => {
    if (!possible) arreter();
  }, [possible, arreter]);
  useEffect(() => arreter, [feuille, arreter]);

  // Les liens de la première, tels que le serveur les rend : pour ne jamais retourner un lien.
  const liens = useQuery({
    queryKey: cles.liens(premiere ?? ''),
    queryFn: () => api.liens(premiere ?? ''),
    enabled: actif && premiere !== null,
  });

  const poser = useMutation({
    mutationFn: (pose: BoutsDuLien & { sorte: SorteDeLien }) => api.lier(pose.sourceId, pose.cibleId, pose.sorte),
    // Un lien neuf, ou le lien de la paire changé : la sorte demandée est la sienne désormais.
    onSuccess: (reponse, pose) => {
      refus.oublier();
      setReliees((n) => n + 1);
      setMessage(reponse.avant ? lienChange(pose.sorte) : RELIEE);
      void relireApres(client, 'poserOuRetirerUneCondition');
    },
    onError: (erreur) => {
      if (estUnCycle(erreur)) {
        refus.montrer(erreur.details as Boucle);
        setMessage(LIEN_BOUCLERAIT);
      } else if (erreur instanceof EchecApi && erreur.code === 'lien_deja_pose') setMessage(DEJA_RELIEE);
      else setMessage(LIEN_PAS_POSE);
    },
  });
  // Une issue de la boucle prise : le lien tenté se repose, un « attend » (seul à pouvoir boucler).
  const refus = useSortieDeBoucle((tente) => poser.mutate({ ...tente, sorte: 'attend' }));
  const fermerLeRefus = () => {
    refus.fermer();
    poser.reset();
  };
  // Échap ferme le refus seul, avant « Lier » qui reste ouvert dessous (comme dans l'inventaire).
  useEchap(refus.boucle !== null, fermerLeRefus, 'avant-tout');

  function cliquer(id: string) {
    if (poser.isPending) return;
    const issue = issueDuClic({ premiere, sorte }, id, liens.data);
    switch (issue.sorte) {
      case 'premiere':
        setPremiere(id);
        setMessage(null);
        return;
      case 'soi':
        setMessage(PAS_A_ELLE_MEME);
        return;
      case 'patiente':
        return;
      case 'autre_sens':
        setMessage(LIEN_DANS_L_AUTRE_SENS);
        return;
      case 'poser':
        setMessage(null);
        poser.mutate({ sourceId: issue.sourceId, cibleId: issue.cibleId, sorte: issue.lien });
    }
  }

  return {
    actif,
    premiere: actif ? premiere : null,
    sorte,
    message,
    reliees,
    /** Le refus d'une boucle est ouvert : J ne coupe pas « Lier » dessous. */
    refusOuvert: refus.boucle !== null,
    refus,
    fermerLeRefus,
    poser,
    /** `sorte` : choisie d'avance par une des trois flèches du bouton (retour d'Igor du 2026-10-24). */
    commencer: (choisie: SorteDeLien | null = null) => {
      arreter();
      fermerLeRefus();
      setActif(true);
      setSorte(choisie);
    },
    arreter,
    cliquer,
    choisirLaSorte: (s: SorteDeLien) => {
      setSorte(s);
      setMessage(null);
    },
  };
}

/** Le bandeau de « Lier », modèle de celui de Hiérarchiser, et le refus d'une boucle. */
export function BarreLier({
  liaison,
  titreDe,
}: {
  liaison: ReturnType<typeof useLiaison>;
  /** Le titre d'une bulle de la carte. */
  titreDe: (id: string) => string;
}) {
  const { premiere, sorte, message, reliees, refus, poser } = liaison;
  const titre = premiere ? titreDe(premiere) : '';

  return (
    <>
      <div className="lier" role="status">
        <div className="lier-mot">
          {premiere === null ? (
            <>
              <strong>{CLIQUE_LA_PREMIERE}</strong>
              <span>{sorte === null ? PUIS_LA_SORTE : PUIS_LES_BULLES}</span>
            </>
          ) : (
            <>
              <strong>{laPremiere(titre)}</strong>
              <span>{sensDuLien(titre, sorte)}</span>
            </>
          )}
          {message && <span className="lier-message">{message}</span>}
        </div>

        {/* Les trois sortes, seulement tant qu'aucune n'est choisie : une sorte choisie (par le bandeau
            ou par une flèche du bord droit) ne se repropose plus ; elle se change par les flèches
            (retour d'Igor du 2026-10-24 : « le bandeau te propose encore les autres liens »). */}
        {premiere !== null && sorte === null && (
          <div className="lier-sortes" role="group" aria-label="sorte de lien">
            {SORTES_DE_LIEN.map((s) => (
              <button
                key={s}
                type="button"
                className="lier-sorte"
                data-sorte={s}
                aria-pressed={sorte === s}
                title={sensDuLien(titre, s)}
                onClick={() => liaison.choisirLaSorte(s)}
              >
                {MOTS_DES_SORTES_A_LIER[s]}
              </button>
            ))}
          </div>
        )}

        {reliees > 0 && <span className="lier-compte">{bullesReliees(reliees)}</span>}

        <div className="lier-fini">
          <button type="button" className="lier-fini-bouton" onClick={liaison.arreter}>
            {FINI}
          </button>
          <span>{TOUCHE_ECHAP_EN_CLAIR}</span>
        </div>
      </div>

      {refus.boucle && (
        <RefusDeCycle
          boucle={refus.boucle}
          geste="lien"
          enCours={refus.sortir.isPending || poser.isPending}
          echec={refus.sortir.isError || (poser.isError && !estUnCycle(poser.error))}
          onFermer={liaison.fermerLeRefus}
          onRetirer={(lienId) => refus.sortir.mutate({ lienId })}
          onInserer={(lienId, titreDuMaillon) => refus.sortir.mutate({ lienId, titre: titreDuMaillon })}
        />
      )}
    </>
  );
}

/**
 * Le bouton « Lier », sur le bord droit de la Carte (retour d'Igor du 2026-10-24 : « un bouton sur
 * la droite avec une flèche, et quand tu la survoles trois flèches différentes pour chaque type de
 * lien ») : la flèche lance « Lier » sans sorte, ou l'arrête s'il est en cours, comme « Fini » et
 * la touche J ; au survol (ou au clavier), trois flèches dessinées comme leur trait sur la carte
 * lancent « Lier » avec leur sorte déjà choisie.
 */
export function BoutonLier({
  actif,
  sorte,
  onBasculer,
  onChoisir,
}: {
  actif: boolean;
  sorte: SorteDeLien | null;
  onBasculer: () => void;
  onChoisir: (sorte: SorteDeLien) => void;
}) {
  return (
    <div className="lier-flottant" role="group" aria-label={LIER}>
      <div className="lier-fleches">
        {SORTES_DE_LIEN.map((s) => (
          <button
            key={s}
            type="button"
            className="lier-fleche"
            data-sorte={s}
            aria-pressed={actif && sorte === s}
            title={`${LIER} : ${MOTS_DES_SORTES_A_LIER[s]}`}
            aria-label={`${LIER} : ${MOTS_DES_SORTES_A_LIER[s]}`}
            onClick={() => onChoisir(s)}
          >
            <FlecheDeLaSorte sorte={s} />
            {/* Son nom, montré au survol (retour d'Igor du 2026-10-24 : « ça te dit quel type de lien c'est »). */}
            <span className="lier-fleche-mot" aria-hidden="true">
              {MOTS_DES_SORTES_A_LIER[s]}
            </span>
          </button>
        ))}
      </div>
      <button
        type="button"
        className="lier-bouton"
        aria-pressed={actif}
        title={LIER_TITRE}
        aria-label={LIER_TITRE}
        onClick={onBasculer}
      >
        <IconeLier className="lier-icone" />
      </button>
    </div>
  );
}

/** Une flèche dessinée comme le trait de sa sorte sur la carte (`dessinDesLiens`) : pointillé fléché, plein fléché, plein sans flèche. */
function FlecheDeLaSorte({ sorte }: { sorte: SorteDeLien }) {
  return (
    <svg className="lier-fleche-dessin" viewBox="0 0 28 12" aria-hidden="true">
      <path d="M2 6 H24" strokeDasharray={sorte === 'attend' ? '3 3' : undefined} />
      {sorte !== 'touche_a' && <path d="M19 2 L25 6 L19 10" />}
    </svg>
  );
}
